import { useState } from "react"
import { useSelectionContext, adminGetClientUsers } from "shared"
import type { Indexed, Client, UserFromDb } from "shared"
import { utils, writeFile } from "xlsx"

const NEW_EXCEL_HEADINGS = [
	"Organisation Correlation ID",
	"Organisation Name",
	"Instance Name",
	"Organisation Customer Reference",
	"Instance Correlation ID",
	"Template Name",
	"Organisation Type",
	"Owner First Name",
	"Owner Last Name",
	"Owner Email Address",
	"Send Owner Invite",
]

const EXISTING_EXCEL_HEADINGS = [
	"Organisation ID",
	"Organisation Correlation ID",
	"Organisation Name",
	"Instance Name",
	"Organisation Customer Reference",
	"Instance Correlation ID",
	"Template Name",
	"Organisation Type",
]

const CONSTANT_FIELDS: { [key: string]: string | number } = {
	"Template Name": "HireAra",
	"Send Owner Invite": 1,
	"Instance Name": "main",
}

const CLIENT_FIELD_MAPPINGS: { [key: string]: string | ((client: Client) => string) } = {
	"Organisation Correlation ID": "cuCode",
	"Organisation ID": "evoOrgId",
	"Organisation Name": (client: Client) => client.sk.slice(5),
	"Organisation Customer Reference": (client: Client) => {
		if (client.cuCode) return client.cuCode
		throw new Error("CuCode is missing for client " + client.sk.split("#")[1])
	},
	"Instance Correlation ID": "client_id",
	"Template Name": null,
	"Organisation Type": (client: Client): "UAT" | "Trial" | "Live" => {
		if (["test", "demo"].includes(client.miscData.split("#")[1])) return "UAT"
		if (client.miscData.split("#")[1] === "free_trial") return "Trial"
		return "Live"
	},
}

async function getOldestAdmin(clientId: string): Promise<UserFromDb | null> {
	const users = (
		(await adminGetClientUsers({ clientId, attributes: ["name", "createdAt", "userRole"] })) as Array<UserFromDb>
	).filter((user) => user.userRole === "admin")
	if (users.length === 0) {
		return null
	}
	const FUTURE_DATE = "10000000000"
	users.sort(
		(a, b) => parseInt((a.createdAt as string) ?? FUTURE_DATE) - parseInt((b.createdAt as string) ?? FUTURE_DATE) // if no createdAt, put at end
	)
	return users[0]
}

async function mapClientToExcelRow(
	client: Client,
	variant: "new" | "existing"
): Promise<{ [key: string]: string | number }> {
	if (client?.evoRef != null) {
		throw new Error("Client " + client.sk.split("#")[1] + " is already in Evo")
	}
	const adminUser = await getOldestAdmin(client.client_id)

	const userFields: { [key: string]: string } =
		variant === "new"
			? {
					"Owner First Name": adminUser?.name.split(" ")[0],
					"Owner Last Name": adminUser?.name.split(" ").slice(1).join(" "),
					"Owner Email Address": adminUser?.email,
				}
			: {}

	const headings = variant === "new" ? NEW_EXCEL_HEADINGS : EXISTING_EXCEL_HEADINGS
	const constant_fields = Object.fromEntries(Object.entries(CONSTANT_FIELDS).filter(([key]) => headings.includes(key)))
	const row = { ...constant_fields, ...userFields }
	for (const heading of headings) {
		const field = CLIENT_FIELD_MAPPINGS[heading]
		if (typeof field === "string") {
			row[heading] = client[field]
		} else if (typeof field === "function") {
			row[heading] = field(client)
		} else if (heading === "Organisation ID") {
			row[heading] = ""
		}
	}
	return row
}

export function EvoExcelButton({
	clients,
	variant = "new",
}: {
	clients: Indexed<Client>
	variant?: "new" | "existing"
}) {
	const { selectedIds } = useSelectionContext()
	const [isLoading, setIsLoading] = useState(false)

	async function downloadExcel() {
		try {
			setIsLoading(true)
			const selectedClients = await Promise.all(
				selectedIds.map((clientId) => mapClientToExcelRow(clients[clientId], variant))
			)
			// output Excel file
			const headings = variant === "new" ? NEW_EXCEL_HEADINGS : EXISTING_EXCEL_HEADINGS
			const ws = utils.json_to_sheet(selectedClients, { header: headings })
			const wb = utils.book_new()
			utils.book_append_sheet(wb, ws, "Evo Migration")
			const downloadExtra =
				selectedClients.length === 1
					? `for_${selectedClients[0]["Organisation Name"]}`
					: new Date().toISOString().split(".")[0]
			const downloadName = `evo_migration_${variant}_${downloadExtra}.xlsx`
			writeFile(wb, downloadName)
			setIsLoading(false)
		} catch (e) {
			alert(`Error creating Excel: ${e}`)
			setIsLoading(false)
			return
		}
	}
	return selectedIds.length === 0 ? null : (
		<button className="SquarerLoaderButton" style={{ maxWidth: 350 }} onClick={downloadExcel} disabled={isLoading}>
			{isLoading ? "Loading..." : `Evo Migration Excel (${variant} organisations)`}
		</button>
	)
}
